<template>
  <input
    :id="id"
    v-bind="$attrs"
    :aria-describedby="ariaDescribedby || undefined"
    :aria-invalid="ariaInvalid || undefined"
    :aria-label="ariaLabel || undefined"
    class="base-input"
    :disabled="disabled"
    :max="max"
    :min="min"
    :placeholder="placeholder"
    :readonly="readonly"
    :required="required"
    :type="type"
    :value="modelValue"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
/**
 * Base input component for text and number entry with v-model support
 *
 * Provides a styled input field with support for various input types,
 * automatic number conversion, and accessibility features.
 *
 * @example Basic text input
 * ```vue
 * <BaseInput v-model="username" placeholder="Enter username" />
 * ```
 *
 * @example Number input with min/max
 * ```vue
 * <BaseInput
 *   v-model="age"
 *   type="number"
 *   :min="0"
 *   :max="120"
 *   placeholder="Age"
 * />
 * ```
 *
 * @example With accessibility labels
 * ```vue
 * <BaseInput
 *   v-model="email"
 *   type="email"
 *   aria-label="Email address"
 *   aria-describedby="email-error"
 *   required
 * />
 * ```
 */
export interface BaseInputProps {
  /** Current value (use with v-model) */
  modelValue: string | number;

  /**
   * Input type determining the data entry format
   * @default 'text'
   */
  type?: 'text' | 'email' | 'number' | 'date' | 'time' | 'password' | 'tel' | 'url';

  /** Placeholder text shown when input is empty */
  placeholder?: string;

  /** Minimum value (for number, date, time types) */
  min?: string | number;

  /** Maximum value (for number, date, time types) */
  max?: string | number;

  /**
   * Disables the input and prevents interaction
   * @default false
   */
  disabled?: boolean;

  /**
   * Makes input read-only (value visible but not editable)
   * @default false
   */
  readonly?: boolean;

  /**
   * Marks field as required for form validation
   * @default false
   */
  required?: boolean;

  /** Unique element ID for label association */
  id?: string;

  /** Accessible label for screen readers (use when visual label not present) */
  ariaLabel?: string;

  /** ID of element describing this input (typically error message ID) */
  ariaDescribedby?: string;

  /** Indicates the input has an error for accessibility */
  ariaInvalid?: boolean;
}

const props = withDefaults(defineProps<BaseInputProps>(), {
  type: 'text',
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = props.type === 'number' && !isNaN(Number(target.value)) ? Number(target.value) : target.value;
  emit('update:modelValue', value);
};
</script>

<style>
.base-input {
  width: var(--input-width);
  padding: var(--input-padding);
  line-height: var(--input-line-height);
  border-width: var(--input-border-width-top) var(--input-border-width-right) var(--input-border-width-bottom)
    var(--input-border-width-left);
  border-style: var(--input-border-style, solid);
  border-color: var(--input-border-color);
  border-radius: var(--input-border-radius-left) var(--input-border-radius-right) var(--input-border-radius-right)
    var(--input-border-radius-left);
  font-family: var(--input-font-family);
  font-size: var(--input-font-size);
  background-color: var(--input-background);
  transition: var(--input-transition);

  &:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring-color);
    outline-offset: var(--focus-ring-offset);
  }

  &:disabled {
    background-color: var(--input-disabled-background);
    cursor: not-allowed;
    opacity: var(--input-disabled-opacity);
  }

  &::placeholder {
    color: var(--color-neutral-500);
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
  }
}
</style>
